"use client";

import { useState } from "react";
import { Quote, ArrowUpRight, ArrowDownRight, TrendingUp } from "lucide-react";
import styles from "./StatsAndTestimonials.module.css";
import Image from "next/image";

interface Testimonial {
  text: string;
  name: string;
  role: string;
  hotel: string;
  image: string;
}

export default function StatsAndTestimonials() {
  const [activeIdx, setActiveIdx] = useState(0);

  const testimonials: Testimonial[] = [
    {
      text: "Moteligo has transformed the way we manage our hotel. From bookings to restaurant billing and website, everything is now in one place.",
      name: "Rajesh Mehta",
      role: "General Manager",
      hotel: "Hotel Grand Palace",
      image: "/images/avatar_rajesh.png",
    },
    {
      text: "The website builder is incredibly easy to use. We launched our direct booking website in a single afternoon and saw our first reservation within hours!",
      name: "Sarah Jenkins",
      role: "Owner",
      hotel: "Ocean Breeze Resort",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=150&auto=format&fit=crop",
    },
    {
      text: "Managing multiple heritage properties was an operational headache. With Moteligo's multi-property dashboard, we run everything seamlessly.",
      name: "Vikram Rathore",
      role: "Managing Director",
      hotel: "Heritage Haveli Hotels",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150&auto=format&fit=crop",
    },
  ];

  return (
    <section className={styles.statsSection} id="testimonials">
      <div className={`${styles.container} container`}>
        <div className={styles.grid}>
          
          {/* Left Column: Analytics Panel */}
          <div className={styles.analyticsColumn}>
            <div className={styles.dashboardCard}>
              <h3 className={styles.cardTitle}>Business at a Glance</h3>
              
              {/* Stats Row */}
              <div className={styles.statsRow}>
                <div className={styles.statCell}>
                  <span className={styles.statLabel}>Total Revenue</span>
                  <span className={styles.statVal}>₹1,24,580</span>
                  <span className={`${styles.statTrend} ${styles.trendUp}`}>
                    <ArrowUpRight size={12} /> +12.5%
                  </span>
                </div>
                <div className={styles.statCell}>
                  <span className={styles.statLabel}>Active Bookings</span>
                  <span className={styles.statVal}>128</span>
                  <span className={`${styles.statTrend} ${styles.trendUp}`}>
                    <ArrowUpRight size={12} /> +8.2%
                  </span>
                </div>
                <div className={styles.statCell}>
                  <span className={styles.statLabel}>Occupied Rooms</span>
                  <span className={styles.statVal}>86</span>
                  <span className={`${styles.statTrend} ${styles.trendUp}`}>
                    <ArrowUpRight size={12} /> +5.0%
                  </span>
                </div>
                <div className={styles.statCell}>
                  <span className={styles.statLabel}>Available Rooms</span>
                  <span className={styles.statVal}>42</span>
                  <span className={`${styles.statTrend} ${styles.trendUp}`}>
                    <ArrowUpRight size={12} /> +3.1%
                  </span>
                </div>
              </div>

              {/* Line Chart */}
              <div className={styles.chartWrapper}>
                <div className={styles.chartHeader}>
                  <h4>Revenue Overview</h4>
                  <span className={styles.chartDropdown}>This Week</span>
                </div>
                
                <div className={styles.svgContainer}>
                  <svg className={styles.chartSvg} viewBox="0 0 400 180" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b46f1" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#3b46f1" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    
                    {/* Grid Lines */}
                    <line x1="0" y1="30" x2="400" y2="30" stroke="#f1f5f9" strokeDasharray="3 3" />
                    <line x1="0" y1="75" x2="400" y2="75" stroke="#f1f5f9" strokeDasharray="3 3" />
                    <line x1="0" y1="120" x2="400" y2="120" stroke="#f1f5f9" strokeDasharray="3 3" />
                    
                    {/* Gradient Fill under path */}
                    <path
                      d="M 10 140 Q 50 140, 70 80 T 130 90 T 190 40 T 250 80 T 310 100 T 370 50 L 370 180 L 10 180 Z"
                      fill="url(#chartGradient)"
                    />
                    
                    {/* Line path */}
                    <path
                      d="M 10 140 Q 50 140, 70 80 T 130 90 T 190 40 T 250 80 T 310 100 T 370 50"
                      fill="none"
                      stroke="#3b46f1"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />

                    {/* Chart Nodes / Dots */}
                    <circle cx="70" cy="80" r="4" fill="#3b46f1" stroke="#ffffff" strokeWidth="1.5" />
                    <circle cx="190" cy="40" r="4" fill="#3b46f1" stroke="#ffffff" strokeWidth="1.5" />
                    <circle cx="370" cy="50" r="4" fill="#3b46f1" stroke="#ffffff" strokeWidth="1.5" />
                  </svg>
                </div>
                
                {/* Chart Axes Labels */}
                <div className={styles.chartXLabels}>
                  <span>Mon</span>
                  <span>Tue</span>
                  <span>Wed</span>
                  <span>Thu</span>
                  <span>Fri</span>
                  <span>Sat</span>
                  <span>Sun</span>
                </div>
              </div>

              {/* Progress Bars */}
              <div className={styles.progressSection}>
                <h4>Top Room Types</h4>
                <div className={styles.progressGrid}>
                  <div className={styles.progressRow}>
                    <div className={styles.progressInfo}>
                      <span>Deluxe Room</span>
                      <span>52%</span>
                    </div>
                    <div className={styles.barContainer}>
                      <div className={`${styles.barFill} ${styles.fillPrimary}`} style={{ width: "52%" }}></div>
                    </div>
                  </div>
                  
                  <div className={styles.progressRow}>
                    <div className={styles.progressInfo}>
                      <span>Executive Suite</span>
                      <span>28%</span>
                    </div>
                    <div className={styles.barContainer}>
                      <div className={`${styles.barFill} ${styles.fillGreen}`} style={{ width: "28%" }}></div>
                    </div>
                  </div>

                  <div className={styles.progressRow}>
                    <div className={styles.progressInfo}>
                      <span>Premium Room</span>
                      <span>20%</span>
                    </div>
                    <div className={styles.barContainer}>
                      <div className={`${styles.barFill} ${styles.fillPurple}`} style={{ width: "20%" }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Testimonials */}
          <div className={styles.testimonialColumn}>
            <div className={styles.quoteCard}>
              <div className={styles.quoteIconWrapper}>
                <Quote className={styles.quoteIcon} size={40} fill="currentColor" />
              </div>
              
              <div className={styles.sliderContainer}>
                {testimonials.map((t, idx) => (
                  <div 
                    key={idx} 
                    className={`${styles.slide} ${idx === activeIdx ? styles.slideActive : ""}`}
                  >
                    <p className={styles.quoteText}>“{t.text}”</p>
                    
                    <div className={styles.authorRow}>
                      <div className={styles.authorAvatar}>
                        <Image
                          src={t.image}
                          alt={t.name}
                          width={48}
                          height={48}
                          style={{ objectFit: "cover", borderRadius: "50%" }}
                        />
                      </div>
                      <div className={styles.authorMeta}>
                        <h4 className={styles.authorName}>{t.name}</h4>
                        <p className={styles.authorRole}>
                          {t.role}, <span className={styles.hotelName}>{t.hotel}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Slider Dots */}
              <div className={styles.dots}>
                {testimonials.map((_, idx) => (
                  <button 
                    key={idx} 
                    className={`${styles.dot} ${idx === activeIdx ? styles.dotActive : ""}`}
                    onClick={() => setActiveIdx(idx)}
                    aria-label={`Go to testimonial ${idx + 1}`}
                  ></button>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
